import {
	CalculatorOutlined,
	CheckOutlined,
	DeleteOutlined,
	InfoCircleOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import { Button, Card, Col, InputNumber, message, Row, Space, Spin, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import type { AppRole } from "@/shared/config/navigation";
import { canManagePaymentSettings } from "@/shared/config/permissions";
import { formatMoney } from "@/shared/utils/money";
import { PaymentsNavigation } from "../components/PaymentsNavigation";
import {
	type FrontendDiscountTier,
	getDiscountTiers,
	saveDiscountTiers,
} from "../services/early-payment-discounts.api";
import { getDiscountPct } from "../utils/payments.utils";

export function DiscountScalePage() {
	const { role } = useOutletContext<{ role: AppRole }>();
	const canManageSettings = canManagePaymentSettings(role);

	const [rows, setRows] = useState<FrontendDiscountTier[]>([]);
	const [initialRows, setInitialRows] = useState<FrontendDiscountTier[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	// Calculator states
	const [calcMonthlyPay, setCalcMonthlyPay] = useState<number>(10_000_000);
	const [calcMonths, setCalcMonths] = useState<number>(6);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		getDiscountTiers()
			.then((tiers) => {
				if (cancelled) {
					return;
				}
				setRows(tiers);
				setInitialRows(tiers);
			})
			.catch((error) => {
				console.error("Failed to load discount tiers:", error);
				message.error("Chegirma shkalasini yuklashda xatolik yuz berdi");
			})
			.finally(() => {
				if (!cancelled) {
					setLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const updateRow = (id: string, patch: Partial<FrontendDiscountTier>) => {
		if (!canManageSettings) {
			message.warning("Ushbu amal uchun ruxsatingiz yo'q.");
			return;
		}
		setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
	};

	const addRow = () => {
		if (!canManageSettings) {
			message.warning("Ushbu amal uchun ruxsatingiz yo'q.");
			return;
		}

		// Propose a starting month based on current rows
		const maxMonthsTo = rows.reduce((max, row) => {
			if (row.monthsTo === null) {
				return max;
			}
			return Math.max(max, row.monthsTo);
		}, 0);

		setRows((current) => [
			...current,
			{
				id: `tier-${Date.now()}`,
				monthsFrom: maxMonthsTo ? maxMonthsTo + 1 : 1,
				monthsTo: null,
				discountPct: 0,
			},
		]);
	};

	const deleteRow = (id: string) => {
		if (!canManageSettings) {
			message.warning("Ushbu amal uchun ruxsatingiz yo'q.");
			return;
		}
		setRows((current) => current.filter((row) => row.id !== id));
	};

	const handleSave = async () => {
		if (!canManageSettings) {
			message.warning("Ushbu amal uchun ruxsatingiz yo'q.");
			return;
		}

		// Validation: check for negative values and overlapping intervals
		const sorted = [...rows]
			.map((row) => ({
				...row,
				monthsFrom: row.monthsFrom || 1,
				monthsTo: row.monthsTo && row.monthsTo > 0 ? row.monthsTo : null,
				discountPct: row.discountPct || 0,
			}))
			.sort((a, b) => a.monthsFrom - b.monthsFrom);

		// Check overlap
		for (let i = 0; i < sorted.length - 1; i++) {
			const current = sorted[i];
			const next = sorted[i + 1];

			if (current.monthsTo === null) {
				message.error(
					`"${current.monthsFrom} oy va undan ko'p" chegara oxirgi bo'lishi kerak. Keyingi intervalni kiritib bo'lmaydi.`
				);
				return;
			}

			if (current.monthsTo >= next.monthsFrom) {
				message.error(
					`Intervallar ustma-ust tushmasligi kerak: ${current.monthsFrom}-${current.monthsTo} va ${next.monthsFrom}-${next.monthsTo ?? "cheksiz"} oylar bir-biriga mos kelmaydi.`
				);
				return;
			}
		}

		setSaving(true);
		try {
			const saved = await saveDiscountTiers(sorted, initialRows);
			setRows(saved);
			setInitialRows(saved);
			message.success("Chegirma shkalasi muvaffaqiyatli saqlandi");
		} catch {
			message.error("Chegirma shkalasini saqlashda xatolik yuz berdi");
		} finally {
			setSaving(false);
		}
	};

	// Detect if rows have changed
	const hasChanges = JSON.stringify(rows) !== JSON.stringify(initialRows);

	// Live calculations
	const subtotal = calcMonthlyPay * calcMonths;
	const activeDiscountPct = getDiscountPct(calcMonths, rows);
	const discountAmount = Math.round(subtotal * (activeDiscountPct / 100));
	const totalToPay = Math.round(subtotal - discountAmount);

	const columns: ColumnsType<FrontendDiscountTier> = [
		{
			title: (
				<Space>
					Oylardan
					<Tooltip title="Chegirma qo'llanishi boshlanadigan minimal oylar soni.">
						<InfoCircleOutlined style={{ color: "var(--crm-text-secondary, #64748b)" }} />
					</Tooltip>
				</Space>
			),
			dataIndex: "monthsFrom",
			key: "monthsFrom",
			width: "30%",
			render: (value: number, record) => (
				<InputNumber
					min={1}
					value={value}
					addonAfter="oy"
					style={{ width: "100%" }}
					onChange={(nextValue) => updateRow(record.id, { monthsFrom: nextValue ?? 1 })}
					disabled={!canManageSettings}
				/>
			),
		},
		{
			title: (
				<Space>
					Oylargacha
					<Tooltip title="Chegirma qo'llaniladigan maksimal oylar soni. Agar yuqori chegara bo'lmasa, bo'sh qoldiring (cheksiz).">
						<InfoCircleOutlined style={{ color: "var(--crm-text-secondary, #64748b)" }} />
					</Tooltip>
				</Space>
			),
			dataIndex: "monthsTo",
			key: "monthsTo",
			width: "30%",
			render: (value: number | null, record) => (
				<InputNumber
					min={1}
					placeholder="cheksiz"
					value={value}
					addonAfter="oy"
					style={{ width: "100%" }}
					onChange={(nextValue) => updateRow(record.id, { monthsTo: nextValue ?? null })}
					disabled={!canManageSettings}
				/>
			),
		},
		{
			title: (
				<Space>
					Chegirma foizi
					<Tooltip title="Erta to'langan to'lov summasidan beriladigan chegirma foizi.">
						<InfoCircleOutlined style={{ color: "var(--crm-text-secondary, #64748b)" }} />
					</Tooltip>
				</Space>
			),
			dataIndex: "discountPct",
			key: "discountPct",
			width: "30%",
			render: (value: number, record) => (
				<InputNumber
					min={0}
					max={100}
					step={0.1}
					value={value}
					addonAfter="%"
					style={{ width: "100%" }}
					onChange={(nextValue) => updateRow(record.id, { discountPct: nextValue ?? 0 })}
					disabled={!canManageSettings}
				/>
			),
		},
		{
			title: "",
			key: "actions",
			width: 80,
			align: "center",
			render: (_, record) => (
				<Button
					danger
					type="text"
					icon={<DeleteOutlined />}
					onClick={() => deleteRow(record.id)}
					disabled={!canManageSettings}
					aria-label="Qatorni o'chirish"
				/>
			),
		},
	];

	return (
		<div className="crm-discount-page">
			<PageHeader
				title="Chegirma shkalasi"
				description="Erta to'lov (muddati kelmagan oylarni oldindan yopish) uchun chegirma foizlarini sozlash"
			/>

			<PaymentsNavigation activeTab="discount-scale" role={role} />

			<Row gutter={[24, 24]}>
				{/* Settings Table Panel */}
				<Col xs={24} lg={15}>
					<Card className="crm-card" title="Chegirmalar jadvali" styles={{ body: { padding: 0 } }}>
						{loading ? (
							<div style={{ textAlign: "center", padding: "48px 0" }}>
								<Spin size="large" />
							</div>
						) : (
							<>
								<Table
									columns={columns}
									dataSource={rows}
									rowKey="id"
									pagination={false}
									scroll={{ x: 550 }}
									size="middle"
									locale={{ emptyText: "Hozircha chegirma tariflari sozlanmagan" }}
								/>
								{canManageSettings && (
									<div style={{ padding: "12px 16px", borderTop: "1px solid #f0f0f0" }}>
										<Button
											type="dashed"
											onClick={addRow}
											block
											icon={<PlusOutlined />}
											style={{ height: "36px", borderRadius: "8px" }}
										>
											Yangi interval qo'shish
										</Button>
									</div>
								)}
							</>
						)}
					</Card>

					{/* Inline Actions Bar below the Table */}
					{canManageSettings && !loading && (
						<div
							style={{
								marginTop: "16px",
								display: "flex",
								alignItems: "center",
								justifyContent: "flex-end",
								flexWrap: "wrap",
								gap: "12px",
								background: "#ffffff",
								padding: "12px 16px",
								borderRadius: "8px",
								border: "1px solid #e2e8f0",
							}}
						>
							{hasChanges && (
								<span
									style={{
										marginRight: "auto",
										color: "var(--crm-primary, #2563eb)",
										display: "flex",
										alignItems: "center",
										gap: "6px",
										fontSize: "13px",
										fontWeight: 500,
									}}
								>
									<span
										style={{
											width: "8px",
											height: "8px",
											borderRadius: "50%",
											background: "var(--crm-primary, #2563eb)",
											display: "inline-block",
										}}
									/>
									Saqlanmagan o'zgarishlar mavjud
								</span>
							)}
							<Button
								disabled={!hasChanges || saving}
								onClick={() => setRows(initialRows)}
								style={{ borderRadius: "8px" }}
							>
								Bekor qilish
							</Button>
							<Button
								type="primary"
								className="crm-primary-btn"
								icon={<CheckOutlined />}
								loading={saving}
								disabled={!hasChanges}
								onClick={handleSave}
								style={{ borderRadius: "8px" }}
							>
								O'zgarishlarni saqlash
							</Button>
						</div>
					)}

					<div
						style={{
							marginTop: "16px",
							padding: "12px 16px",
							background: "#f0f9ff",
							border: "1px solid #e0f2fe",
							borderRadius: "8px",
							display: "flex",
							gap: "12px",
							alignItems: "flex-start",
						}}
					>
						<InfoCircleOutlined style={{ color: "#0284c7", fontSize: "16px", marginTop: "2px" }} />
						<div style={{ fontSize: "13px", color: "#0369a1", lineHeight: "1.5" }}>
							<strong style={{ display: "block", marginBottom: "2px", color: "#0f172a" }}>
								Qoidalar
							</strong>
							Tizim mijoz muddatli shartnoma bo'yicha bir vaqtda to'laydigan oylari soniga qarab
							chegirma foizini topadi. Masalan, 6 oy to'langanda 6-11 oylik intervaldagi chegirma
							qo'llaniladi.
						</div>
					</div>
				</Col>

				{/* Helper and Live Calculator Panel */}
				<Col xs={24} lg={9}>
					<Card
						className="crm-card"
						title={
							<Space>
								<CalculatorOutlined />
								Kalkulyator (Sinov)
							</Space>
						}
						styles={{ body: { padding: "16px 20px" } }}
					>
						<div style={{ marginBottom: "12px" }}>
							<span
								style={{
									display: "block",
									fontSize: "12px",
									fontWeight: 500,
									color: "#475569",
									marginBottom: "4px",
								}}
							>
								Oylik to'lov summasi (UZS)
							</span>
							<InputNumber
								min={1}
								value={calcMonthlyPay}
								onChange={(val) => setCalcMonthlyPay(val ?? 0)}
								style={{ width: "100%" }}
								addonAfter={<span style={{ whiteSpace: "nowrap" }}>UZS</span>}
								formatter={(value) => `${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
								parser={(value) => Number((value ?? "").replace(/\s/g, ""))}
							/>
						</div>

						<div style={{ marginBottom: "16px" }}>
							<span
								style={{
									display: "block",
									fontSize: "12px",
									fontWeight: 500,
									color: "#475569",
									marginBottom: "4px",
								}}
							>
								Oldindan to'lanadigan oylar soni
							</span>
							<InputNumber
								min={1}
								value={calcMonths}
								onChange={(val) => setCalcMonths(val ?? 1)}
								style={{ width: "100%" }}
								addonAfter="oy"
							/>
						</div>

						<div
							style={{
								background: "#f8fafc",
								padding: "12px 14px",
								borderRadius: "8px",
								border: "1px solid #f1f5f9",
							}}
						>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "6px",
									fontSize: "13px",
								}}
							>
								<span style={{ color: "#64748b" }}>Hisoblangan summa:</span>
								<strong style={{ color: "#334155" }}>{formatMoney(subtotal)} UZS</strong>
							</div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "6px",
									fontSize: "13px",
								}}
							>
								<span style={{ color: "#64748b" }}>Chegirma foizi:</span>
								<strong style={{ color: "var(--crm-green, #10b981)" }}>{activeDiscountPct}%</strong>
							</div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "10px",
									fontSize: "13px",
								}}
							>
								<span style={{ color: "#64748b" }}>Tejalgan summa (Chegirma):</span>
								<strong style={{ color: "var(--crm-red, #ef4444)" }}>
									-{formatMoney(discountAmount)} UZS
								</strong>
							</div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									borderTop: "1px solid #e2e8f0",
									paddingTop: "10px",
									alignItems: "baseline",
								}}
							>
								<span
									style={{ fontWeight: 600, fontSize: "13px", color: "var(--crm-text-primary)" }}
								>
									Mijoz to'laydi:
								</span>
								<strong style={{ fontSize: "16px", color: "var(--crm-primary, #2563eb)" }}>
									{formatMoney(totalToPay)} UZS
								</strong>
							</div>
						</div>
					</Card>
				</Col>
			</Row>
		</div>
	);
}

export default DiscountScalePage;
